import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import {
  configureStore,
  createListenerMiddleware,
  TypedStartListening,
  TypedAddListener,
  ListenerEffectAPI,
  addListener,
  createSelector,
} from '@reduxjs/toolkit';
import { globalSlice } from './slices/states/global/globalSlice';
import { routerSlice } from './slices/states/router/routerSlice';
import { KPISlice } from './slices/states/kpi/KPISlice';
import { requestSlice } from './slices/functionals/request/requestSlice';
import deepCopyMiddleware from './middleware/deepCopyMiddleware';

const listenerMiddlewareInstance = createListenerMiddleware({
  onError: () => console.error,
});

const store = configureStore({
  reducer: {
    [globalSlice.name]: globalSlice.reducer,
    [routerSlice.name]: routerSlice.reducer,
    [requestSlice.name]: requestSlice.reducer,
    [KPISlice.name]: KPISlice.reducer,
  },
  middleware: gDM => gDM({
    serializableCheck: {
      ignoredPaths: ['global.notification'],
      ignoredActions: ['global/notification'],
      warnAfter: 200,
    },
    immutableCheck: { warnAfter: 200 },
  }).prepend(listenerMiddlewareInstance.middleware).concat(deepCopyMiddleware),
});

export { store };

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// @see https://redux-toolkit.js.org/usage/usage-with-typescript#getting-the-dispatch-type
export type AppDispatch = typeof store.dispatch;

export type AppListenerEffectAPI = ListenerEffectAPI<RootState, AppDispatch>;

// @see https://redux-toolkit.js.org/api/createListenerMiddleware#typescript-usage
export type AppStartListening = TypedStartListening<RootState, AppDispatch>;
export type AppAddListener = TypedAddListener<RootState, AppDispatch>;

export const startAppListening = listenerMiddlewareInstance.startListening as AppStartListening;
export const addAppListener = addListener as AppAddListener;

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch = () => useDispatch<AppDispatch>();

//#region AppSelectors
const myUseAppSelector = selector => {
  //our redux will never return undefined
  const defaultObject = {};

  return useSelector(state => {
    const result = selector(state);

    return result === undefined ? defaultObject : result;
  });
};

type SelectorReturnType<T, C> = T extends any[] ? C[] : C;

export const myUseClassSelector = <T, C>(
  selector: (state: RootState) => T,
  ClassType: new (data: any) => C
): SelectorReturnType<T, C> => {
  // Crear un selector memoizado
  const memoizedSelector = createSelector(selector, (result: T) => {
    // Si el resultado es null, undefined o un array vacío, devolverlo tal cual
    if (result === undefined || result === null || (Array.isArray(result) && result.length === 0)) {
      return result as SelectorReturnType<T, C>;
    }

    // Verificar si el resultado es un array
    if (Array.isArray(result)) {
      return result.map(data => new ClassType(data)) as SelectorReturnType<T, C>;
    } else {
      return new ClassType(result) as SelectorReturnType<T, C>;
    }
  });

  // Usar el selector memoizado
  return useSelector<RootState, SelectorReturnType<T, C>>(memoizedSelector);
};
//#endregion
export const useObjectSelector: TypedUseSelectorHook<RootState> = myUseAppSelector;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
export const useClassSelector = myUseClassSelector;
